import { faSliders } from "@fortawesome/free-solid-svg-icons/faSliders";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Tooltip } from "../../../utils/local-antd";
import { TeamRole } from "../../../model/permissions/types/TeamRole.enum";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { ThemeType } from "../../../theme/Theme";
import { TEAM_ROLE_STYLE } from "../utils/teamRoleStyle";

const Badge = styled.span<{ $color?: keyof ThemeType }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  white-space: nowrap;
  padding: 0 ${Spacing.s};
  border-radius: ${Rounded.md};
  background-color: ${({ theme, $color }) =>
    $color
      ? `color-mix(in srgb, ${theme[$color]} 14%, transparent)`
      : theme.sub};
  color: ${({ theme }) => theme.textColor};
  border: 1px solid
    ${({ theme, $color }) =>
      $color
        ? `color-mix(in srgb, ${theme[$color]} 55%, transparent)`
        : theme.darkSub};
  font-size: 0.8rem;
`;

const CustomizedIcon = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: help;
`;

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

interface TeamRoleLabelProps {
  role: TeamRole;
}

export const TeamRoleLabel: React.FC<TeamRoleLabelProps> = ({ role }) => {
  const { t } = useTranslation();
  const { icon, color } = TEAM_ROLE_STYLE[role];

  return (
    <Label>
      <AtiraIcon icon={icon} color={color} width="1em" />
      {t(`permissions.team-role.${role}`)}
    </Label>
  );
};

interface TeamRoleBadgeProps {
  role: TeamRole;
  dataTestId?: string;
}

export const TeamRoleBadge: React.FC<TeamRoleBadgeProps> = ({
  role,
  dataTestId,
}) => (
  <Badge $color={TEAM_ROLE_STYLE[role].color} data-testid={dataTestId}>
    <TeamRoleLabel role={role} />
  </Badge>
);

interface CustomizedBadgeProps {
  dataTestId?: string;
}

export const CustomizedPermissionsBadge: React.FC<CustomizedBadgeProps> = ({
  dataTestId,
}) => {
  const { t } = useTranslation();

  return (
    <Tooltip title={t("permissions.team-role.customized.tooltip")}>
      <CustomizedIcon
        role="img"
        aria-label={t("permissions.team-role.customized")}
        data-testid={dataTestId}
      >
        <AtiraIcon icon={faSliders} color="main" />
      </CustomizedIcon>
    </Tooltip>
  );
};
